import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { testConnection, runAssessment } from '../services/api';
import { type AssessmentResult, type AssessmentRequest } from '../types/assessment';

interface Props {
  onComplete: (result: AssessmentResult) => void;
  onError: (error: string) => void;
}

const REGIONS = [
  { value: 'us-1', label: 'US-1 (Commercial)' },
  { value: 'us-2', label: 'US-2 (Commercial)' },
  { value: 'eu-1', label: 'EU-1' },
  { value: 'us-gov-1', label: 'US-GOV-1 (Government)' },
];

const API_PERMISSIONS = [
  { scope: 'devices:read', description: 'Read endpoint inventory and sensor details' },
  { scope: 'detections:read', description: 'Read detection and alert data' },
  { scope: 'policies:read', description: 'Read prevention policy configuration' },
  { scope: 'host-groups:read', description: 'Read host group configuration' },
  { scope: 'ioa-exclusions:read', description: 'Read IOA exclusion settings' },
  { scope: 'ml-exclusions:read', description: 'Read ML exclusion settings' },
  { scope: 'sensor-visibility-exclusions:read', description: 'Read sensor visibility exclusions' },
];

export default function NewAssessment({ onComplete, onError }: Props) {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    customerName: '',
    assessorName: '',
    region: 'us-1',
    clientId: '',
    clientSecret: '',
    cid: '',
    assessmentPeriodDays: 30,
  });
  
  const [isTesting, setIsTesting] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [testStatus, setTestStatus] = useState<{ success: boolean; message: string } | null>(null);
  const [progress, setProgress] = useState(0);

  const handleChange = (field: string, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setTestStatus(null);
  };

  const buildRequest = (): AssessmentRequest => ({
    customerName: formData.customerName,
    assessorName: formData.assessorName,
    credentials: {
      clientId: formData.clientId,
      clientSecret: formData.clientSecret,
      region: formData.region,
      cid: formData.cid || undefined,
    },
    assessmentPeriodDays: formData.assessmentPeriodDays,
  });

  const handleTestConnection = async () => {
    if (!formData.clientId || !formData.clientSecret) {
      onError('Client ID and Client Secret are required');
      return;
    }

    setIsTesting(true);
    setTestStatus(null);
    
    try {
      const result = await testConnection(buildRequest());
      setTestStatus({ success: result.success, message: result.message });
    } catch (err) {
      setTestStatus({ 
        success: false, 
        message: err instanceof Error ? err.message : 'Connection test failed' 
      });
    } finally {
      setIsTesting(false);
    }
  };

  const handleRunAssessment = async () => {
    if (!formData.customerName || !formData.assessorName) {
      onError('Customer name and assessor name are required');
      return;
    }

    if (!formData.clientId || !formData.clientSecret) {
      onError('Client ID and Client Secret are required');
      return;
    }

    setIsRunning(true);
    setProgress(10);
    
    try {
      setProgress(30);
      const result = await runAssessment(buildRequest());
      setProgress(100);
      onComplete(result);
      // Navigation is handled by App.tsx route when assessmentResult changes
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Assessment failed');
      setProgress(0);
    } finally {
      setIsRunning(false);
    }
  };

  const handleClear = () => {
    setFormData({
      customerName: '',
      assessorName: '',
      region: 'us-1',
      clientId: '',
      clientSecret: '',
      cid: '',
      assessmentPeriodDays: 30,
    });
    setTestStatus(null);
    setProgress(0);
  };

  return (
    <div className="card">
      <h2>New Assessment</h2>
      
      <div className="form-row">
        <div className="form-group">
          <label htmlFor="customerName">Customer / Organization Name *</label>
          <input
            id="customerName"
            type="text"
            value={formData.customerName}
            onChange={e => handleChange('customerName', e.target.value)}
            placeholder="Acme Corporation"
            disabled={isRunning}
          />
        </div>
        
        <div className="form-group">
          <label htmlFor="assessorName">Assessor Name *</label>
          <input
            id="assessorName"
            type="text"
            value={formData.assessorName}
            onChange={e => handleChange('assessorName', e.target.value)}
            placeholder="John Smith"
            disabled={isRunning}
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="region">CrowdStrike Cloud Region *</label>
          <select
            id="region"
            value={formData.region}
            onChange={e => handleChange('region', e.target.value)}
            disabled={isRunning}
          >
            {REGIONS.map(r => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>
        </div>
        
        <div className="form-group">
          <label htmlFor="assessmentPeriod">Assessment Period (days)</label>
          <input
            id="assessmentPeriod"
            type="number"
            min={1}
            max={90}
            value={formData.assessmentPeriodDays}
            onChange={e => handleChange('assessmentPeriodDays', parseInt(e.target.value) || 30)}
            disabled={isRunning}
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="clientId">API Client ID *</label>
          <input
            id="clientId"
            type="text"
            value={formData.clientId}
            onChange={e => handleChange('clientId', e.target.value)}
            placeholder="Your CrowdStrike API Client ID"
            disabled={isRunning}
            autoComplete="off"
          />
        </div>
        
        <div className="form-group">
          <label htmlFor="clientSecret">API Client Secret *</label>
          <input
            id="clientSecret"
            type="password"
            value={formData.clientSecret}
            onChange={e => handleChange('clientSecret', e.target.value)}
            placeholder="Your CrowdStrike API Client Secret"
            disabled={isRunning}
            autoComplete="off"
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="cid">Customer CID (optional)</label>
          <input
            id="cid"
            type="text"
            value={formData.cid}
            onChange={e => handleChange('cid', e.target.value)}
            placeholder="Optional - will be auto-detected"
            disabled={isRunning}
          />
        </div>
      </div>

      {testStatus && (
        <div className={`alert ${testStatus.success ? 'alert-success' : 'alert-error'}`} style={{
          padding: '0.75rem 1rem',
          borderRadius: 'var(--radius)',
          marginBottom: '1rem',
          background: testStatus.success ? '#e6f4ea' : '#fce8e8',
          color: testStatus.success ? '#1e8e3e' : '#d93025',
          border: `1px solid ${testStatus.success ? '#34a853' : '#f5c6cb'}`,
        }}>
          {testStatus.message}
        </div>
      )}

      {isRunning && (
        <div style={{ marginBottom: '1rem' }}>
          <div className="progress-bar">
            <div className="progress-bar-fill" style={{ width: `${progress}%` }} />
          </div>
          <p style={{ fontSize: '0.875rem', color: 'var(--color-text-muted)', marginTop: '0.5rem' }}>
            Running assessment... {progress}%
          </p>
        </div>
      )}

      <div className="btn-group">
        <button
          className="btn btn-secondary"
          onClick={handleTestConnection}
          disabled={isTesting || isRunning || !formData.clientId || !formData.clientSecret}
        >
          {isTesting ? 'Testing...' : 'Test Connection'}
        </button>
        
        <button
          className="btn btn-primary"
          onClick={handleRunAssessment}
          disabled={isRunning || !formData.customerName || !formData.assessorName || !formData.clientId || !formData.clientSecret}
        >
          {isRunning ? 'Running Assessment...' : 'Run Assessment'}
        </button>
        
        <button
          className="btn btn-danger"
          onClick={handleClear}
          disabled={isRunning}
        >
          Clear Session
        </button>
      </div>

      {/* Required API Permissions */}
      <details style={{ marginTop: '1.5rem' }}>
        <summary style={{ cursor: 'pointer', fontSize: '0.875rem', fontWeight: 600, color: 'var(--color-primary)' }}>
          Required CrowdStrike API Permissions
        </summary>
        <div style={{ marginTop: '0.75rem', padding: '0.75rem', background: '#f8f9fa', borderRadius: 'var(--radius)', fontSize: '0.8125rem' }}>
          <p style={{ marginBottom: '0.5rem', color: 'var(--color-text-muted)' }}>
            Your CrowdStrike API Client must have the following read-only scopes to run this assessment:
          </p>
          <ul style={{ paddingLeft: '1.25rem', margin: 0 }}>
            {API_PERMISSIONS.map((perm, idx) => (
              <li key={idx} style={{ marginBottom: '0.25rem' }}>
                <code style={{ background: '#e9ecef', padding: '0.125rem 0.375rem', borderRadius: '4px', fontFamily: 'monospace' }}>{perm.scope}</code>
                {' — '}{perm.description}
              </li>
            ))}
          </ul>
          <p style={{ marginTop: '0.5rem', color: '#856404', fontSize: '0.75rem' }}>
            <strong>Note:</strong> This application performs READ-ONLY operations. No changes will be made to your CrowdStrike environment.
          </p>
        </div>
      </details>

      <div style={{ marginTop: '1rem', padding: '1rem', background: '#f8f9fa', borderRadius: 'var(--radius)', fontSize: '0.8125rem', color: 'var(--color-text-muted)' }}>
        <strong>Security Notice:</strong> Your credentials are sent directly to the Cloudflare Worker for authentication and are never stored in browser storage, databases, or logs. They exist only in memory during this session.
      </div>
    </div>
  );
}
